import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  Loader2,
  MapPin,
  Truck,
  FileText,
  CheckCircle2,
  Search,
  X,
  CalendarRange,
  Home
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';

const MobileJobs = () => {
  const navigate = useNavigate();
  const { api } = useMobileAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Search state
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchStartDate, setSearchStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchEndDate, setSearchEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Get date label based on selected date
  const getDateLabel = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    
    const diffDays = Math.round((selected - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === -1) return 'Yesterday';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    
    // For other dates, show the formatted date
    return format(selectedDate, 'EEE, d MMM');
  };

  // Check if selected date is today
  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return today.getTime() === selected.getTime();
  };

  useEffect(() => {
    if (!searchMode) {
      fetchJobs();
    }
  }, [selectedDate, searchMode]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      // Format the selected date for the API
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/api/mobile/jobs?date=${dateStr}`);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchStartDate || !searchEndDate) return;
    
    setSearching(true);
    try {
      const response = await api.get(`/api/mobile/jobs/search/date-range?start_date=${searchStartDate}&end_date=${searchEndDate}`);
      setSearchResults(response.data);
      setSearchMode(true);
      setSearchDialogOpen(false);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchMode(false);
    setSearchResults([]);
    setSearchStartDate(format(new Date(), 'yyyy-MM-dd'));
    setSearchEndDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const navigateDate = (direction) => {
    if (direction === 1) {
      setSelectedDate(addDays(selectedDate, 1));
    } else {
      setSelectedDate(subDays(selectedDate, 1));
    }
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.slice(0, 5);
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return format(date, 'EEE, d MMM');
  };

  const getStatusBadge = (job) => {
    if (job.has_docket) {
      return <Badge className="bg-green-900 text-green-300 text-xs">Docketed</Badge>;
    }
    if (job.status === 'confirmed') {
      return <Badge className="bg-blue-900 text-blue-300 text-xs">Confirmed</Badge>;
    }
    if (job.status === 'invoiced') {
      return <Badge className="bg-purple-900 text-purple-300 text-xs">Invoiced</Badge>;
    }
    return <Badge variant="secondary" className="text-xs">{job.status}</Badge>;
  };

  // Determine which jobs to display
  const displayJobs = searchMode ? searchResults : jobs;
  const isLoading = searchMode ? searching : loading;

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/mobile/dashboard')} className="text-gray-400 p-2">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-white font-bold text-lg">My Jobs</h1>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSearchDialogOpen(true)}
              className="text-[#ffd108] p-2"
              data-testid="search-jobs-btn"
            >
              <Search className="h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/mobile/dashboard')}
              className="text-gray-400 p-2"
              data-testid="home-btn"
            >
              <Home className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Date Selector or Search Results Header */}
      {searchMode ? (
        <div className="px-4 py-3 bg-[#252525] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-[#ffd108]" />
            <span className="text-white font-medium text-sm">
              {formatDateDisplay(searchStartDate)} - {formatDateDisplay(searchEndDate)}
            </span>
            <Badge className="bg-[#ffd108]/20 text-[#ffd108] text-xs ml-2">
              {searchResults.length} job{searchResults.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearSearch}
            className="text-gray-400 p-1"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="px-4 py-3 bg-[#252525]">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigateDate(-1)}
              className="text-gray-400"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#ffd108]" />
              <span className="text-white font-medium">{getDateLabel()}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigateDate(1)}
              className="text-gray-400"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
          {/* Today button - only show if not on today's date */}
          {!isToday() && (
            <div className="flex justify-center mt-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={goToToday}
                className="text-[#ffd108] text-xs h-7 px-3"
              >
                <Calendar className="h-3 w-3 mr-1" />
                Today
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
          </div>
        ) : displayJobs.length === 0 ? (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardContent className="py-8 text-center">
              <Calendar className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">
                {searchMode 
                  ? 'No jobs found in this date range' 
                  : `No jobs for ${getDateLabel().toLowerCase()}`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {displayJobs.map((job, index) => {
              // Group header for search mode - show date when it changes
              const showDateHeader = searchMode && (
                index === 0 || 
                job.start_date !== displayJobs[index - 1]?.start_date
              );
              
              // For multi-day jobs, show an indicator
              const isMultiDay = job.end_date && job.end_date !== job.start_date;
              
              // Determine the date to pass to job detail
              // For regular view, use the selected date
              // For search mode, use job's start_date or viewing_date
              const navDate = searchMode 
                ? job.viewing_date || job.start_date  // From search results
                : job.viewing_date || format(selectedDate, 'yyyy-MM-dd');  // From regular list
              
              return (
                <div key={`${job.id}-${job.viewing_date || job.start_date}`}>
                  {showDateHeader && (
                    <div className="flex items-center gap-2 mb-2 mt-4 first:mt-0">
                      <Calendar className="h-4 w-4 text-[#ffd108]" />
                      <span className="text-[#ffd108] font-semibold text-sm">
                        {formatDateDisplay(job.start_date)}
                      </span>
                    </div>
                  )}
                  <Card 
                    className="bg-[#2a2a2a] border-gray-700 cursor-pointer hover:border-[#ffd108] transition-colors"
                    onClick={() => navigate(`/mobile/jobs/${job.id}?date=${navDate}`)}
                    data-testid={`job-card-${job.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[#ffd108] font-bold">
                              {formatTime(job.start_time)}{job.end_time ? ` - ${formatTime(job.end_time)}` : ''}
                            </span>
                            {getStatusBadge(job)}
                            {isMultiDay && (
                              <Badge className="bg-purple-900 text-purple-300 text-xs">
                                Multi-day
                              </Badge>
                            )}
                            {job.operator_role === 'additional_worker' && job.worker_type_name && (
                              <Badge className="bg-blue-900 text-blue-300 text-xs">
                                {job.worker_type_name}
                              </Badge>
                            )}
                          </div>
                          <h3 className="text-white font-medium text-lg">{job.customer_name}</h3>
                          <p className="text-gray-400 text-sm flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3" />
                            {job.site_name}
                          </p>
                          <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                            <Truck className="h-3 w-3" />
                            {job.equipment_name} ({job.equipment_asset_id})
                          </p>
                          {job.description && (
                            <p className="text-gray-500 text-xs mt-2 line-clamp-2">{job.description}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {job.has_docket && (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          )}
                          <ChevronRight className="h-5 w-5 text-gray-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Date Range Search Dialog */}
      <Dialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen}>
        <DialogContent className="bg-[#2a2a2a] border-gray-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-[#ffd108]" />
              Search Jobs by Date Range
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Start Date</Label>
              <Input
                type="date"
                value={searchStartDate}
                onChange={(e) => setSearchStartDate(e.target.value)}
                className="bg-[#1a1a1a] border-gray-600 text-white font-mono"
                data-testid="search-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">End Date</Label>
              <Input
                type="date"
                value={searchEndDate}
                onChange={(e) => setSearchEndDate(e.target.value)}
                min={searchStartDate}
                className="bg-[#1a1a1a] border-gray-600 text-white font-mono"
                data-testid="search-end-date"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                className="flex-1 border-gray-600 text-gray-300"
                onClick={() => setSearchDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
                onClick={handleSearch}
                disabled={searching || !searchStartDate || !searchEndDate}
                data-testid="search-submit-btn"
              >
                {searching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileJobs;
